import { format, isToday, startOfWeek, addDays } from "date-fns";
import { Clock, Plus } from "lucide-react";
import { COLORS } from "@/lib/api";
import { CheckCircle } from "@/components/shared";

const EventBlock = ({ e, onClick }) => (
  <button data-testid={`week-event-${e.id}`} onClick={onClick}
    className="w-full text-left rounded-lg px-2.5 py-2 text-white text-xs transition-transform duration-200 hover:-translate-y-0.5"
    style={{ backgroundColor: COLORS[e.color] || COLORS.terracotta }}>
    <p className="font-medium leading-snug break-words">{e.title}</p>
    {e.time && <p className="opacity-85 flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" />{e.time}</p>}
  </button>
);

const TaskRow = ({ t, onToggle }) => (
  <div data-testid={`week-task-${t.id}`} className="flex items-start gap-2 rounded-lg bg-t-card border border-dashed px-2 py-1.5"
    style={{ borderColor: COLORS[t.list.color] || COLORS.terracotta }}>
    <div className="scale-75 origin-top-left -mr-1"><CheckCircle checked={t.done} onClick={onToggle} color={COLORS[t.list.color]} testId={`week-task-check-${t.id}`} /></div>
    <span className={`text-xs leading-snug break-words ${t.done ? "line-through text-t-faint" : "text-t-ink"}`}>{t.text}</span>
  </div>
);

export const WeekView = ({ cursor, selected, onSelect, byDate, tasksOn, showTodos, onEventClick, onAdd, onToggleTask }) => {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="week-view">
      {days.map((d) => {
        const key = format(d, "yyyy-MM-dd");
        const evs = byDate(key);
        const tks = tasksOn(key);
        const active = key === selected;
        return (
          <div key={key} data-testid={`week-day-${key}`} onClick={() => onSelect(key)}
            className={`rounded-xl border p-2.5 min-h-[120px] md:min-h-[380px] flex flex-col gap-2 cursor-pointer transition-[background-color,border-color] duration-200 ${
              active ? "bg-t-primary-soft border-t-primary/60" : "bg-t-card/60 border-t-surface hover:bg-t-card hover:border-t-line"
            }`}>
            <div className="flex md:flex-col items-center md:items-start justify-between gap-1 pb-2 border-b border-t-line/70">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-t-muted">{format(d, "EEE")}</p>
              <span className={`font-serif-display text-2xl font-semibold leading-none h-9 w-9 flex items-center justify-center rounded-full ${isToday(d) ? "bg-t-primary text-white" : ""}`}>{format(d, "d")}</span>
            </div>
            <div className="space-y-1.5">
              {evs.map((e) => <EventBlock key={e.id} e={e} onClick={(ev) => { ev.stopPropagation(); onEventClick(e); }} />)}
            </div>
            {showTodos && tks.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-t-c2">Chores</p>
                {tks.map((t) => <TaskRow key={t.id} t={t} onToggle={(ev) => { ev?.stopPropagation?.(); onToggleTask(t); }} />)}
              </div>
            )}
            <button data-testid={`week-add-${key}`} onClick={(ev) => { ev.stopPropagation(); onAdd(key); }}
              className="mt-auto flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] text-t-muted border border-dashed border-t-line-strong opacity-60 hover:opacity-100 hover:bg-t-surface transition-opacity">
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        );
      })}
    </div>
  );
};

export const weekLabel = (cursor) => {
  const s = startOfWeek(cursor);
  const e = addDays(s, 6);
  return s.getMonth() === e.getMonth() ? `${format(s, "MMM d")} – ${format(e, "d, yyyy")}` : `${format(s, "MMM d")} – ${format(e, "MMM d, yyyy")}`;
};
